import React, { useState, useEffect } from "react";
import { useCart } from "./CartContext";
import { useLocation, useNavigate } from "react-router-dom";
import { AIRTABLE_API_KEY, AIRTABLE_URL } from "./constants";

export default function CartButton({ setActiveService }) {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const [showCart, setShowCart] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [liveOrder, setLiveOrder] = useState(null);
  const navigate = useNavigate();
  const phoneRegex = /^[0-9]{10}$/;
  const [errors, setErrors] = useState({ phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const generateOrderId = () => {
    const shortId = Date.now().toString().slice(-6);
    return `ORD${shortId}`;
  };

  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    address: "",
    state: "",
    pincode: "",
    phone: "",
    landmark: "",
    slotDate: "",
    slotHour: "",
    slotMinute: "",
    slotPeriod: "",
  });

  // ✅ control phone field editability
  const [isPhoneReadOnly, setIsPhoneReadOnly] = useState(false);

  // ✅ On mount → check localStorage for phone
  useEffect(() => {
    const savedPhone = localStorage.getItem("userPhone");
    if (savedPhone) {
      setCustomerDetails((prev) => ({
        ...prev,
        phone: savedPhone, // auto-fill
      }));
      setIsPhoneReadOnly(true); // make it uneditable
    }
  }, []);

  // ✅ Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Agar phone number new dal raha hai aur valid hai to localStorage me save kar dena
    if (name === "phone" && phoneRegex.test(value)) {
      localStorage.setItem("userPhone", value);
    }
  };

  const location = useLocation();
  const editingBooking = location.state?.editingBooking;

  useEffect(() => {
    if (editingBooking) {
      const parsedServices = editingBooking.servicesFormatted?.split(", ") || [];

      const rebuiltCart = parsedServices.map((entry) => {
        const [name, rest] = entry.split(" - ₹");
        const [priceStr, qtyStr] = rest.split(" x ");
        return {
          name: name.trim(),
          price: parseInt(priceStr),
          quantity: parseInt(qtyStr),
        };
      });

      const firstServiceName = rebuiltCart[0]?.name.toLowerCase() || "";

      if (firstServiceName.includes("ac")) {
        setActiveService("acservice");
      } else if (firstServiceName.includes("gas") || firstServiceName.includes("repair")) {
        setActiveService("repairgas");
      } else if (firstServiceName.includes("install")) {
        setActiveService("installation");
      }

      const slotParts = editingBooking.slot?.split(" at ") || [];
      const [date, timePart] = slotParts;
      const [time, period] = timePart?.split(" ") || [];
      const [hour, minute] = time?.split(":") || [];

      setCustomerDetails({
        name: editingBooking.name,
        address: editingBooking.address,
        state: editingBooking.state,
        pincode: editingBooking.pincode,
        landmark: editingBooking.landmark,
        phone: editingBooking.phone,
        slotDate: date?.split("-").reverse().join("-") || "",
        slotHour: hour || "",
        slotMinute: minute || "",
        slotPeriod: period || "",
      });

      clearCart();
      rebuiltCart.forEach((item) => cartItems.push(item));
      setShowCart(false);
      setShowForm(false);
    }
  }, [editingBooking]);

  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleSubmit = async () => {
  if (isSubmitting) return; // prevent multiple clicks

  const requiredFields = [
    "name",
    "address",
    "state",
    "pincode",
    "phone",
    "slotDate",
    "slotHour",
    "slotMinute",
    "slotPeriod"
  ];

  const missingFields = requiredFields.filter(field => !customerDetails[field]?.trim());

  if (missingFields.length > 0) {
    alert(`Please fill all required fields.`);
    return;
  }

  if (!phoneRegex.test(customerDetails.phone)) {
    alert("Phone must be exactly 10 digits");
    return;
  }

  setIsSubmitting(true); // 🔵 Disable submit button

  try {
    const isEditing = !!editingBooking;
    const orderId = isEditing ? editingBooking.orderId : generateOrderId();

    const formattedSlot = customerDetails.slotDate
      ? `${customerDetails.slotDate.split("-").reverse().join("-")} at ${customerDetails.slotHour}:${customerDetails.slotMinute} ${customerDetails.slotPeriod}`
      : "";

    const serviceTotal = cartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const extraPartsTotal = customerDetails.extraPartsTotal
      ? parseInt(customerDetails.extraPartsTotal)
      : 0;

    const grandTotal = serviceTotal + extraPartsTotal;

    const selectedServices = cartItems
      .map((item) => `${item.name} - ₹${item.price} x ${item.quantity}`)
      .join(", ");

    try {
  const headers = {
    Authorization: `Bearer ${AIRTABLE_API_KEY}`,
    "Content-Type": "application/json",
  };

  const formattedSlot = `${customerDetails.slotDate} at ${customerDetails.slotHour}:${customerDetails.slotMinute} ${customerDetails.slotPeriod}`;
  const orderId = editingBooking?.orderId || generateOrderId();

  const fullOrder = {
    orderId,
    name: customerDetails.name,
    address: customerDetails.address,
    state: customerDetails.state,
    pincode: customerDetails.pincode,
    phone: customerDetails.phone,
    landmark: customerDetails.landmark,
    slot: formattedSlot,
    services: cartItems.map((item) => `${item.name} - ₹${item.price} x ${item.quantity}`).join(", "),
    serviceTotal: totalPrice,
    extraPartsTotal: 0,
    grandTotal: totalPrice,
    timestamp: new Date().toLocaleString(),
  };

  // 1️⃣ Check if phone exists & orderId is blank
  const checkResp = await fetch(
    `https://api.airtable.com/v0/appEFkSoRcyfQG0aW/Sheet1?filterByFormula=AND({phone}="${customerDetails.phone}", {orderId}="")`,
    { headers }
  );
  const checkData = await checkResp.json();
  const existingRow = checkData.records[0];

  if (existingRow) {
    // ✅ PATCH existing row
    await fetch(`https://api.airtable.com/v0/appEFkSoRcyfQG0aW/Sheet1/${existingRow.id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ fields: fullOrder }),
    });
    console.log("✅ Updated existing row in Sheet1");
  } else {
    // ➕ POST new row
    await fetch(`https://api.airtable.com/v0/appEFkSoRcyfQG0aW/Sheet1`, {
      method: "POST",
      headers,
      body: JSON.stringify({ fields: fullOrder }),
    });
    console.log("✅ Created new row in Sheet1");
  }

  // Save booking to localStorage
  const existingHistory = JSON.parse(localStorage.getItem("bookingHistory")) || [];

  const newBookingEntry = {
    ...fullOrder,
    services: cartItems.map((item) => item.name),
    servicesFormatted: fullOrder.services,
    totalPrice,
  };

  const updatedHistory = editingBooking
    ? existingHistory.map((entry) => entry.orderId === orderId ? newBookingEntry : entry)
    : [...existingHistory, newBookingEntry];

  localStorage.setItem("bookingHistory", JSON.stringify(updatedHistory));

  setSubmitMessage("✅ Booking submitted!");
  clearCart();
  setCustomerDetails({
    name: "", address: "", state: "", pincode: "", phone: "",
    landmark: "", slotDate: "", slotHour: "", slotMinute: "", slotPeriod: "",
  });
  setShowForm(false);
  navigate("/my-bookings");

} catch (err) {
  console.error("❌ Submission error:", err);
  alert("Something went wrong, please try again.");
}


    clearCart();
    navigate("/my-booking");
  } catch (err) {
    console.error("Error during submit:", err);
    alert("Something went wrong. Please try again.");
  } finally {
    setIsSubmitting(false); // 🔵 Re-enable button
  }
};

  const submitToAirtable = async (orderData) => {
  try {
    // Step 1: Try to fetch the existing record by orderId
    const fetchRes = await fetch(`${AIRTABLE_URL}?filterByFormula=${encodeURIComponent(`{orderId} = "${orderData.orderId}"`)}`, {
      headers: {
        Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        "Content-Type": "application/json",
      },
    });

    const fetchData = await fetchRes.json();
    const existingRecord = fetchData.records?.[0];

    let response;

    if (existingRecord) {
      // Step 2: If found, update it with PATCH
      const recordId = existingRecord.id;

      response = await fetch(`${AIRTABLE_URL}/${recordId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: orderData,
        }),
      });
    } else {
      // Step 3: If not found, create new with POST
      response = await fetch(AIRTABLE_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: orderData,
        }),
      });
    }

    if (!response.ok) {
      const errData = await response.json();
      console.error("Airtable Error Details:", JSON.stringify(errData, null, 2));
      alert(`Airtable submission failed: ${errData.error?.message}`);
    } else {
      alert(existingRecord ? "Order updated successfully!" : "Order submitted successfully!");
    }
  } catch (error) {
    console.error("Network or other error:", error);
    alert("Error occurred while submitting to Airtable");
  }
};


  useEffect(() => {
    const interval = setInterval(() => {
      const orderId = localStorage.getItem("lastOrderId");
      if (!orderId) return;

      fetch(`${AIRTABLE_URL}?filterByFormula=${encodeURIComponent(`{orderId} = "${orderId}"`)}`, {
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          const record = data.records?.[0];
          if (record) {
            const fields = record.fields;
            setLiveOrder({
              ...fields,
              workCompleted: fields.workCompleted?.toString().toLowerCase() === "true",
            });
          }
        })
        .catch((err) => console.error("Polling error:", err));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ position: "fixed", bottom: 20, left: 0, right: 0, padding: "0 16px", zIndex: 9999 }}>
      <button
        style={{
          backgroundColor: "blue",
          color: "white",
          padding: "15px",
          borderRadius: "10px",
          width: "100%",
          fontSize: "16px",
        }}
        onClick={() => {
          if (cartItems.length === 0) {
            alert("Please add at least one service");
            return;
          }
          setShowCart(!showCart);
        }}
      >
        View Cart ({cartItems.length}) - ₹{totalPrice}
      </button>

      {showCart && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: "80vh",
            overflowY: "auto",
            backgroundColor: "#fff",
            borderTop: "2px solid #ccc",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.2)",
            zIndex: 9999,
            padding: "20px",
            borderRadius: "16px 16px 0 0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowCart(false)}
              style={{
                position: "absolute",
          top: 10,
          right: 10,
          background: "none",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "Black",
        }}
      >
        ✖
      </button>
          </div>

          <h3>Your Cart</h3>
          {cartItems.length === 0 && <p>Cart is empty</p>}
          <ul>
            {cartItems.map((item) => (
              <li key={item.name} style={{ marginBottom: "10px" }}>
                <b>{item.name}</b> - ₹{item.price} x {item.quantity}
                <button
                  style={{ marginLeft: "10px", color: "red" }}
                  onClick={() => removeFromCart(item.name)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <p><b>Total: ₹{totalPrice}</b></p>

          <button
            onClick={() => setShowForm(true)}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              padding: "10px",
              borderRadius: "6px",
              width: "100%",
              marginTop: "10px",
            }}
          >
            Add Address and Slot
          </button>

          {showForm && (
            <div style={{ marginTop: "15px" }}>
              <input
                placeholder="Customer Name"
                value={customerDetails.name}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, name: e.target.value })
                }
                style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
              />
              <input
                placeholder="Address"
                value={customerDetails.address}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, address: e.target.value })
                }
                style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
              />
              <input
                placeholder="State"
                value={customerDetails.state}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, state: e.target.value })
                }
                style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
              />
              <input
  name="postalcode"
  autoComplete="off"
  inputMode="numeric"
  placeholder="Pin code"
  value={customerDetails.pincode}
  onChange={(e) =>
    setCustomerDetails({ ...customerDetails, pincode: e.target.value })
  }
  style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
/>

              <input
                placeholder="Landmark"
                value={customerDetails.landmark}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, landmark: e.target.value })
                }
                style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
              />

  <input
   type="tel"
      name="phone"
      inputMode="numeric"
      maxLength={10}
      placeholder="Phone Number"
      value={customerDetails.phone}
      onChange={!isPhoneReadOnly ? handleInputChange : undefined}
      readOnly={isPhoneReadOnly}                                  // ✅ Make uneditable
     style={{
     width: "100%",
        marginBottom: "10px",
        padding: "8px",
        border: errors.phone ? "1px solid red" : undefined,
        backgroundColor: isPhoneReadOnly ? "#f0f0f0" : "white",
        color: isPhoneReadOnly ? "#555" : "black",                           // ✅ Dimmed text
  }}
/>

              {/* Slot Picker */}
              <div style={{ marginBottom: "10px" }}>
                <label><b>Choose Slot:</b></label>
                <input
                  type="date"
                  value={customerDetails.slotDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setCustomerDetails({ ...customerDetails, slotDate: e.target.value })
                  }
                  style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
                />

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <select
                    value={customerDetails.slotHour}
                    onChange={(e) =>
                      setCustomerDetails({ ...customerDetails, slotHour: e.target.value })
                    }
                    style={{ width: "32%", padding: "8px" }}
                  >
                    <option value="">Hour</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>

                  <select
                    value={customerDetails.slotMinute}
                    onChange={(e) =>
                      setCustomerDetails({ ...customerDetails, slotMinute: e.target.value })
                    }
                    style={{ width: "32%", padding: "8px" }}
                  >
                    <option value="">Minute</option>
                    {["00", "15", "30", "45"].map((min) => (
                      <option key={min} value={min}>{min}</option>
                    ))}
                  </select>

                  <select
                    value={customerDetails.slotPeriod}
                    onChange={(e) =>
                      setCustomerDetails({ ...customerDetails, slotPeriod: e.target.value })
                    }
                    style={{ width: "32%", padding: "8px" }}
                  >
                    <option value="">AM/PM</option>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <button
  onClick={handleSubmit}
  disabled={isSubmitting}
  style={{
    backgroundColor: isSubmitting ? "#6c757d" : "#007bff",
    color: "white",
    padding: "10px",
    width: "100%",
    borderRadius: "6px",
    cursor: isSubmitting ? "not-allowed" : "pointer",
  }}
>
  {isSubmitting ? "Submitting..." : "Final Submit"}
</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
